import type { FAQ } from './types';

/**
 * English customer-audience FAQs. Mirror of customer.es.tsx — same
 * `id` slugs so deep links (#login, #compliance-tcpa, etc) resolve in
 * either language. When adding a new FAQ, add the entry to BOTH files
 * with the same id; the HelpContent loader will pick the right one
 * based on i18n.resolvedLanguage.
 */
export const CUSTOMER_FAQS_EN: FAQ[] = [
  // =============================================================================
  // Getting started
  // =============================================================================
  {
    id: 'login',
    category: 'getting_started',
    question: 'How do I sign in?',
    tags: ['login', 'access'],
    answer: (
      <p>
        Go to the login page and enter the email + password your admin invited
        you with. If you've never signed in, you should have an email titled
        "You've been invited to EzSender" — click it to set your password
        the first time.
      </p>
    ),
  },
  {
    id: 'forgot-password',
    category: 'getting_started',
    question: 'I forgot my password — how do I recover it?',
    tags: ['reset', 'recover'],
    cta: { label: 'Recover password', to: '/forgot-password' },
    answer: (
      <p>
        On the login screen click "Forgot your password?". You'll get an email
        with a link valid for 1 hour to set a new one.
      </p>
    ),
  },
  {
    id: 'change-password',
    category: 'getting_started',
    question: 'How do I change my password?',
    tags: ['security', 'rotate'],
    cta: { label: 'Go to Security', to: '/settings?tab=security' },
    answer: (
      <p>
        Settings → <strong>Security</strong> tab → enter the current and the
        new password. Changing your password automatically signs you out on
        all other devices.
      </p>
    ),
  },
  {
    id: 'invite-team',
    category: 'getting_started',
    question: 'How do I invite a teammate?',
    tags: ['team', 'users', 'collaborators'],
    cta: { label: 'Go to Team', to: '/settings?tab=team' },
    answer: (
      <p>
        Settings → <strong>Team</strong> → "Invite member". They'll receive an
        email to set their password. Only Owner and Admin roles can invite.
      </p>
    ),
  },
  {
    id: 'roles',
    category: 'getting_started',
    question: 'What does each role do (Owner, Admin, Operator, Viewer)?',
    tags: ['permissions', 'access'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Owner</strong>: workspace owner. Immutable: role can't be changed and the user can't be deleted.</li>
        <li><strong>Admin</strong>: manages workspace, team, sender IDs, API keys, billing.</li>
        <li><strong>Operator</strong>: day-to-day usage — sends SMS, imports contacts, launches campaigns. No settings or billing access.</li>
        <li><strong>Viewer</strong>: read-only. Sees dashboards and reports but can't send anything.</li>
      </ul>
    ),
  },

  // =============================================================================
  // Senders (10DLC)
  // =============================================================================
  {
    id: 'sender-what',
    category: 'senders',
    question: 'What is a Sender ID?',
    tags: ['from', 'sender', 'number'],
    answer: (
      <p>
        It's the phone number or alphanumeric name your recipients see your
        SMS coming from. Before sending you need at least one approved Sender
        ID for your account.
      </p>
    ),
  },
  {
    id: 'sender-10dlc',
    category: 'senders',
    question: 'Why do I need 10DLC in the US?',
    tags: ['10dlc', 'a2p', 'us', 'long code'],
    answer: (
      <p>
        US carriers (T-Mobile, AT&amp;T, Verizon) require 10DLC registration
        for all A2P (application-to-person) SMS sent from long codes. Without
        registration, carriers filter or block your messages. Registration
        takes 1-3 weeks and the platform handles it.
      </p>
    ),
  },
  {
    id: 'sender-approval-time',
    category: 'senders',
    question: 'How long does sender approval take?',
    tags: ['10dlc', 'approval', 'submitted'],
    answer: (
      <p>
        Depends on the type: <strong>10DLC long code</strong> takes 1-3 weeks,
        <strong> toll-free</strong> ~5 business days, <strong>short code</strong> 4-6
        weeks. While in "submitted" or "pending" state you can't use it for
        sending.
      </p>
    ),
  },
  {
    id: 'sender-rejected',
    category: 'senders',
    question: 'My sender ended up "rejected" — what now?',
    tags: ['10dlc', 'rejected'],
    answer: (
      <p>
        It means the carrier or platform admin rejected your campaign for
        content / use case reasons. The admin will reach out with the
        specific reason. Most common: undocumented opt-in flow, or messaging
        that sounds like SHAFT (sex / hate / alcohol / firearms / tobacco).
      </p>
    ),
  },

  // =============================================================================
  // Contacts
  // =============================================================================
  {
    id: 'contacts-import',
    category: 'contacts',
    question: 'How do I import contacts from Excel or CSV?',
    tags: ['xlsx', 'csv', 'upload', 'list'],
    cta: { label: 'Go to Contacts', to: '/contacts' },
    answer: (
      <p>
        Contacts → "Import Excel/CSV" → select your file. The first row must
        contain column names (you can use them as message variables). The
        importer detects which column is the phone and normalizes to E.164.
        Supports up to 1 million rows per file.
      </p>
    ),
  },
  {
    id: 'contacts-variables',
    category: 'contacts',
    question: 'How do I use variables in messages (e.g. {{Name}})?',
    tags: ['merge', 'mailmerge', 'personalize'],
    answer: (
      <p>
        Column names from your Excel/CSV become variables. If your file has a
        <code className="mono"> Name </code>column, write
        <code className="mono"> {`Hi {{Name}}, your order...`}</code> in the
        message and it gets replaced per contact. Missing variables in a row
        render as blank.
      </p>
    ),
  },
  {
    id: 'contacts-duplicates',
    category: 'contacts',
    question: 'Why do some contacts end up as "duplicate" on import?',
    tags: ['duplicate', 'dedupe'],
    answer: (
      <p>
        The importer compares the normalized phone (E.164) against what's
        already in the list. If it exists, it's not re-inserted — that's
        intentional to avoid sending the same SMS twice. The post-import
        summary tells you how many were skipped.
      </p>
    ),
  },
  {
    id: 'contacts-dnc',
    category: 'contacts',
    question: 'What is the DNC (do-not-contact) list?',
    tags: ['dnc', 'opt-out', 'stop'],
    cta: { label: 'View DNC', to: '/inbound' },
    answer: (
      <p>
        When a recipient replies STOP, their number is automatically added
        to your DNC list. Any future attempt to send them an SMS is rejected
        with code <code className="mono">phone_in_dnc</code>. The list is
        per-tenant — it's not shared across customers.
      </p>
    ),
  },

  // =============================================================================
  // Sending SMS
  // =============================================================================
  {
    id: 'send-single-vs-campaign',
    category: 'sending',
    question: 'What is the difference between a single message and a campaign?',
    tags: ['send', 'bulk'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Single message</strong>: 1 recipient, 1 send. Useful for tests, OTP, individual alerts.</li>
        <li><strong>Campaign</strong>: 1 message to a whole list of contacts, with variables. Queued in the background — you can close the tab and it keeps running.</li>
      </ul>
    ),
  },
  {
    id: 'send-segments',
    category: 'sending',
    question: 'How many segments does my message cost?',
    tags: ['segments', 'price', 'length'],
    answer: (
      <p>
        SMS is billed per segment, not per message. <strong>GSM-7</strong>
        (plain text without accents): 160 chars in 1 segment, 153 chars/segment
        for multi-part. <strong>UCS-2</strong> (accents / emoji / special
        characters): 70 chars in 1 segment, 67 chars/segment for multi-part.
        The composer shows live counts.
      </p>
    ),
  },
  {
    id: 'send-encoding',
    category: 'sending',
    question: 'GSM-7 vs UCS-2 — when does it switch?',
    tags: ['emoji', 'accents', 'encoding', 'characters'],
    answer: (
      <p>
        If every character fits in GSM-7 (basic ASCII + a few specials like
        @, $, &amp;), your message uses GSM-7. The moment you include an accent
        (á, ñ), an emoji 🎉, or a non-ASCII character, it auto-switches to
        UCS-2 — and the same chars that fit in 160 now fit in 70. Worth
        considering for long messages.
      </p>
    ),
  },
  {
    id: 'send-schedule',
    category: 'sending',
    question: 'Can I schedule a campaign for later?',
    tags: ['scheduled', 'future', 'schedule'],
    answer: (
      <p>
        Yes. When creating a campaign, instead of "Launch now" pick
        "Schedule". Set date + time + timezone. The campaign sits in
        "scheduled" state and the daemon launches it at the chosen moment.
        You can cancel the schedule before it kicks off.
      </p>
    ),
  },
  {
    id: 'send-cancel',
    category: 'sending',
    question: 'How do I cancel or pause a running campaign?',
    tags: ['stop', 'pause'],
    answer: (
      <p>
        "Cancel" button in the campaigns list or detail page. The campaign
        stops queueing new messages immediately. The ones already in the
        worker queue can take a few extra seconds to stop. Messages already
        sent to the carrier can't be recalled.
      </p>
    ),
  },

  // =============================================================================
  // Compliance
  // =============================================================================
  {
    id: 'compliance-tcpa',
    category: 'compliance',
    question: 'What is the TCPA window and why is it blocking my sends?',
    tags: ['tcpa', 'time', 'hours'],
    answer: (
      <p>
        TCPA (US) bans SMS marketing outside 8am-9pm <strong>recipient time</strong>{' '}
        (not yours). The platform checks the destination number's timezone
        and blocks sends outside that window — the message ends up in
        "rejected" state with code <code className="mono">outside_tcpa_window</code>.
        To skip this for transactional / 2FA traffic, use a <code className="mono">message_type</code>
        other than "marketing".
      </p>
    ),
  },
  {
    id: 'compliance-stop-help',
    category: 'compliance',
    question: 'How do STOP and HELP keywords work?',
    tags: ['opt-out', 'unsubscribe'],
    answer: (
      <p>
        When a recipient replies STOP (or variants: CANCEL, UNSUBSCRIBE,
        QUIT), the platform:
        {' '}<strong>(1)</strong> adds their number to your DNC,
        {' '}<strong>(2)</strong> sends a confirmation auto-reply,
        {' '}<strong>(3)</strong> blocks future sends to that number.
        HELP responds with contact info. It's TCPA-mandatory — you can't
        disable it.
      </p>
    ),
  },
  {
    id: 'compliance-opt-in',
    category: 'compliance',
    question: 'Do I need explicit opt-in from my contacts?',
    tags: ['consent'],
    answer: (
      <p>
        Yes. TCPA requires express written consent before sending marketing
        SMS. For transactional traffic (order shipped, OTP, alerts) implicit
        opt-in via the existing business relationship is enough. Document
        your opt-in flow — carriers ask for it during 10DLC registration and
        a TCPA lawsuit can demand it.
      </p>
    ),
  },

  // =============================================================================
  // Billing
  // =============================================================================
  {
    id: 'billing-how-charged',
    category: 'billing',
    question: 'How is SMS billed?',
    tags: ['price', 'cost'],
    answer: (
      <p>
        Each segment is billed at the price your rate card defines for the
        recipient's prefix. The charge happens <strong>before</strong> the send —
        if your balance can't cover it, the message ends up "rejected". If
        the carrier rejects it after the charge (rare), an automatic refund
        is issued.
      </p>
    ),
  },
  {
    id: 'billing-topup',
    category: 'billing',
    question: 'How do I top up my balance?',
    tags: ['topup', 'pay', 'balance'],
    cta: { label: 'Go to Billing', to: '/billing' },
    answer: (
      <div className="space-y-2">
        <p>
          Billing → <strong>Top up</strong> (or the button on the balance widget). Choose an amount, then a payment method:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Card</strong> — instant, via Stripe's secure checkout. Your balance updates automatically the moment the payment clears.</li>
          <li><strong>PayPal, Zelle, Crypto (USDT), or Wire / ACH</strong> — the panel shows the exact payment details. After you pay, email your receipt and we credit the balance manually.</li>
        </ul>
        <p>
          Every top-up appears as a <code className="mono">deposit</code> in your transaction history.
        </p>
      </div>
    ),
  },
  {
    id: 'billing-low-balance',
    category: 'billing',
    question: 'What happens when my balance gets low?',
    tags: ['alert', 'warning'],
    answer: (
      <p>
        When your balance drops below the threshold (default $10), you get
        an email + an in-app banner. If it hits $0, new sends are rejected
        with <code className="mono">insufficient_balance</code> until you
        top up. In-flight sends that already charged complete normally.
      </p>
    ),
  },
  {
    id: 'billing-plan',
    category: 'billing',
    question: 'What plan am I on and what does it include?',
    tags: ['subscription'],
    cta: { label: 'View my plan', to: '/billing' },
    answer: (
      <p>
        In Billing you can see your current plan, monthly fee (if any),
        included SMS, and overage cost. Plans are created by your reseller —
        contact them directly to change.
      </p>
    ),
  },

  // =============================================================================
  // Reports
  // =============================================================================
  {
    id: 'reports-statuses',
    category: 'reports',
    question: 'What does each status (queued / sent / delivered / failed) mean?',
    tags: ['status', 'state', 'message'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>queued</strong>: accepted, waiting for the worker.</li>
        <li><strong>sent</strong>: the carrier (Twilio/etc) accepted it. Delivery not yet confirmed.</li>
        <li><strong>delivered</strong>: the carrier confirmed delivery to the phone.</li>
        <li><strong>failed</strong>: rejected at some step. The error_code field tells you why.</li>
        <li><strong>rejected</strong>: the platform blocked it (TCPA, DNC, balance, sender not approved, etc) before sending it to the carrier.</li>
      </ul>
    ),
  },
  {
    id: 'reports-delivery-rate',
    category: 'reports',
    question: 'How do I see my delivery rate by country?',
    tags: ['kpi', 'rate', 'metrics'],
    cta: { label: 'Go to Reports', to: '/reports' },
    answer: (
      <p>
        Reports → pick a date range → look at the "Top countries by volume"
        block + the KPIs above (Total / Delivered / Failed). Delivery % is
        computed as delivered / (delivered + failed), excluding rejected.
      </p>
    ),
  },
  {
    id: 'reports-export',
    category: 'reports',
    question: 'Can I export my messages to CSV?',
    tags: ['csv', 'download', 'export'],
    answer: (
      <p>
        Yes. Reports → "Export CSV" exports every message in the chosen
        range. There's also an "Export" button on each campaign detail page
        for that campaign's messages. CSVs include all status fields +
        error code for external analysis.
      </p>
    ),
  },

  // =============================================================================
  // Troubleshooting
  // =============================================================================
  {
    id: 'trouble-sms-rejected',
    category: 'troubleshooting',
    question: 'My SMS ended up "rejected" — why?',
    tags: ['error', 'rejected'],
    answer: (
      <p>
        Look at the error_code column in Messages. The most common ones:
        {' '}<code className="mono">sender_not_approved</code> (10DLC pending),
        {' '}<code className="mono">outside_tcpa_window</code> (outside 8am-9pm),
        {' '}<code className="mono">phone_in_dnc</code> (recipient opted out),
        {' '}<code className="mono">insufficient_balance</code> (no funds),
        {' '}<code className="mono">no_route_available</code> (no rate card for that country).
      </p>
    ),
  },
  {
    id: 'trouble-no-emails',
    category: 'troubleshooting',
    question: "I'm not getting platform emails",
    tags: ['email', 'spam', 'inbox'],
    answer: (
      <p>
        Check <strong>Spam / Promotions</strong> first. If it's not there,
        confirm the email on your account is correct in Settings → Workspace.
        Emails come from <code className="mono">noreply@getezsender.com</code> —
        add it to your contacts to avoid future filtering. If they still don't
        arrive, contact your admin.
      </p>
    ),
  },
  {
    id: 'trouble-cant-login',
    category: 'troubleshooting',
    question: "I can't sign in even though my password is correct",
    tags: ['login', 'access', '401'],
    answer: (
      <p>
        Three common causes: <strong>(1)</strong> CAPS LOCK is on,
        {' '}<strong>(2)</strong> your password manager is autofilling an old
        password — try in incognito and type it manually,
        {' '}<strong>(3)</strong> your account is deactivated (ask your admin).
        If all that checks out, "Forgot your password?" resets it.
      </p>
    ),
  },
  {
    id: 'trouble-suspended',
    category: 'troubleshooting',
    question: 'My account says "suspended" — what happened?',
    tags: ['suspended', 'blocked'],
    answer: (
      <p>
        Your reseller suspended the account — usually for overdue balance
        or a policy violation. Contact your account manager. Sends are
        blocked but the data (contacts, history, settings) is intact.
      </p>
    ),
  },
  {
    id: 'trouble-api-keys',
    category: 'troubleshooting',
    question: 'How do I create an API key to integrate my backend?',
    tags: ['api', 'integration', 'key'],
    cta: { label: 'Go to Developers', to: '/developers' },
    answer: (
      <p>
        Developers → "New API key" → create with a descriptive name. Only
        Admin+ can create them. <strong>Important:</strong> the full secret is
        shown ONLY ONCE at creation. Copy it to your password manager
        immediately — afterwards you only see the last 4 chars.
      </p>
    ),
  },
  {
    id: 'trouble-webhooks',
    category: 'troubleshooting',
    question: 'How do I receive webhooks when an SMS is delivered?',
    tags: ['webhook', 'callback', 'integration'],
    cta: { label: 'Go to Webhooks', to: '/webhooks' },
    answer: (
      <p>
        Webhooks → "New endpoint" → your public URL + events to subscribe to
        (delivery, failure, inbound, etc). Each POST is HMAC-SHA256 signed
        so you can verify it came from us. Auto-retried with exponential
        backoff if your endpoint returns 5xx.
      </p>
    ),
  },

  // =============================================================================
  // Voice — outbound campaigns (Press-1 / IVR broadcast)
  // =============================================================================
  {
    id: 'voice-what',
    category: 'voice',
    question: 'What can I do with the Voice module?',
    tags: ['voice', 'press-1', 'ivr', 'broadcast', 'phone'],
    cta: { label: 'Open Voice campaigns', to: '/voice/campaigns' },
    answer: (
      <div className="space-y-2">
        <p>
          Voice lets you run two kinds of phone-call jobs from EzSender:
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Outbound Press-1 / IVR broadcasts</strong> — your contact list gets a call that plays a greeting, captures a key press (e.g. "Press 1 to be connected"), and routes accordingly.</li>
          <li><strong>Inbound IVR</strong> — callers dial your DID (your business phone number) and your flow answers — menu, business-hours check, voicemail, AI agent, etc.</li>
        </ul>
        <p>
          Both share the same building blocks (flows, audio assets, DIDs, rate cards) so you can mix and match.
        </p>
      </div>
    ),
  },
  {
    id: 'voice-flow-builder',
    category: 'voice',
    question: 'How do I build an IVR flow?',
    tags: ['ivr', 'flow', 'canvas', 'builder', 'press-1'],
    cta: { label: 'Open Flows', to: '/voice/flows' },
    answer: (
      <div className="space-y-2">
        <p>
          IVR → <strong>Flows</strong> → "New flow". Pick inbound or outbound at the start — that decides which node palette the canvas shows.
        </p>
        <p>
          Drag nodes onto the canvas (Play audio, Press to capture digits, Transfer, AI Agent, Voicemail, etc.) and wire them together. Click any node to edit its settings in the right-side Inspector.
        </p>
        <p>
          Save creates a new version. Each save bumps the version number so you can roll back. Toggle <strong>Use v2-native runtime</strong> in settings to unlock the advanced nodes (Webhook, Condition, Record, dynamic TTS with <code>{`{{contact.X}}`}</code> variables).
        </p>
      </div>
    ),
  },
  {
    id: 'voice-dids',
    category: 'voice',
    question: 'What is a DID and how do I add one?',
    tags: ['did', 'caller-id', 'number', 'phone'],
    cta: { label: 'Open DIDs', to: '/voice/dids' },
    answer: (
      <div className="space-y-2">
        <p>
          A DID is a phone number you own — used both as the <strong>caller ID</strong> on outbound campaigns and as an <strong>inbound number</strong> that customers can dial to reach your IVR.
        </p>
        <p>
          IVR → <strong>DIDs</strong> → "Add DID" and enter the number in E.164 format (+15551234567). You can register the number, but only the platform operator can mark it <strong>verified</strong> — that requires LOA / carrier confirmation on file.
        </p>
        <p>
          Until a DID is verified+active, voice campaigns and inbound flows can't use it.
        </p>
      </div>
    ),
  },
  {
    id: 'voice-audio-assets',
    category: 'voice',
    question: 'How do I add audio greetings / prompts?',
    tags: ['audio', 'wav', 'mp3', 'tts', 'greeting'],
    cta: { label: 'Open Audio Assets', to: '/voice/assets' },
    answer: (
      <div className="space-y-2">
        <p>
          Two ways:
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Upload</strong> a WAV or MP3 file from IVR → Audio Assets. Best for branded recordings.</li>
          <li><strong>TTS</strong> (text-to-speech) — write the text + pick a voice; we render the audio for you. Supports <code>{`{{contact.first_name}}`}</code> style variables when the flow uses the v2-native runtime.</li>
        </ul>
        <p>
          Once uploaded/rendered, the asset is available to drop into any Play node in your flow editor.
        </p>
      </div>
    ),
  },
  {
    id: 'voice-launch-campaign',
    category: 'voice',
    question: 'How do I launch a voice campaign?',
    tags: ['campaign', 'launch', 'broadcast', 'press-1'],
    cta: { label: 'Open Campaigns', to: '/voice/campaigns' },
    answer: (
      <div className="space-y-2">
        <p>
          Voice → <strong>Campaigns</strong> → "New campaign". Pick a flow, a contact list, a caller ID (a fixed DID, or a rotation pool with local presence — see <em>Caller ID pools</em>), set the pacing (calls/minute), and optionally turn on AMD. Save as draft.
        </p>
        <p>
          From the detail page click <strong>Launch now</strong> or schedule for later. Before launching, use the <strong>Test call</strong> button to dial your own phone and verify the flow end-to-end.
        </p>
      </div>
    ),
  },
  {
    id: 'voice-caller-id-pools',
    category: 'voice',
    question: 'What are Caller ID pools and local presence?',
    tags: ['caller-id', 'pool', 'local presence', 'rotation', 'area code'],
    cta: { label: 'Open Caller ID pools', to: '/voice/caller-id-pools' },
    answer: (
      <div className="space-y-2">
        <p>
          A Caller ID pool is a group of your numbers that a campaign rotates through — a different caller ID per call — instead of always showing one fixed number. Create one under Voice → <strong>Caller ID pools</strong>, then paste or import your numbers.
        </p>
        <p>When you build a campaign you pick a <strong>Caller ID strategy</strong>:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Fixed</strong> — always present one DID you choose.</li>
          <li><strong>Random</strong> — a random number from the pool on every call.</li>
          <li><strong>Local presence</strong> — prefers a pool number in the callee's own area code, then their state, then random. Showing a local number lifts answer rates.</li>
        </ul>
        <p>
          With a pool strategy you don't need to pick a fixed DID — the pool supplies the number automatically.
        </p>
      </div>
    ),
  },
  {
    id: 'voice-amd',
    category: 'voice',
    question: 'What is AMD (answering-machine detection)?',
    tags: ['amd', 'voicemail', 'answering machine', 'detection'],
    cta: { label: 'Open Campaigns', to: '/voice/campaigns' },
    answer: (
      <div className="space-y-2">
        <p>
          AMD tries to tell a live person from a voicemail / answering machine right after the call is answered. Turn it on per campaign in the <strong>Answering machine</strong> section. When a machine is detected you choose what happens:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Continue the live flow</strong> (default) — the greeting plays anyway and AMD only annotates the report. Safest option.</li>
          <li><strong>Hang up</strong> — end the call to save spend on machines.</li>
          <li><strong>Drop voicemail &amp; hang up</strong> — wait for the beep, play a pre-recorded message, then hang up.</li>
        </ul>
        <p>
          Detection is approximate — with late carrier audio it can occasionally misread a real person, which is why <em>Continue</em> is the default. The result is saved on each call for your reports.
        </p>
      </div>
    ),
  },

  // =============================================================================
  // AI Agents
  // =============================================================================
  {
    id: 'ai-agent-what',
    category: 'ai_agents',
    question: 'What is an AI Agent?',
    tags: ['ai', 'agent', 'voice', 'gpt', 'claude'],
    cta: { label: 'Open AI Agents', to: '/voice/ai-agents' },
    answer: (
      <div className="space-y-2">
        <p>
          An AI Agent is a conversational voice assistant — when a caller reaches an <code>ai_agent</code> node in your inbound flow (or you embed one in an outbound campaign), the AI listens, understands, responds in natural language, and can perform actions (book appointments, look up customer info, transfer to a human, etc).
        </p>
        <p>
          You "train" the agent without code: you describe the business, the agent's identity, what it should and shouldn't say, the languages it speaks. We compile that into a system prompt for the underlying LLM (Claude Haiku by default).
        </p>
      </div>
    ),
  },
  {
    id: 'ai-agent-create',
    category: 'ai_agents',
    question: 'How do I create an AI Agent for my business?',
    tags: ['ai', 'agent', 'create', 'profile'],
    cta: { label: 'Open AI Agents', to: '/voice/ai-agents' },
    answer: (
      <div className="space-y-2">
        <p>
          IVR → <strong>AI Agents</strong> → "New profile". Fill in:
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Identity</strong>: agent name, languages, persona.</li>
          <li><strong>Business</strong>: name, what you sell, hours, location.</li>
          <li><strong>Knowledge</strong>: paste FAQ snippets or use the URL ingester to grab content from your existing website.</li>
          <li><strong>Behavior</strong>: things the agent should never say, escalation rules.</li>
        </ul>
        <p>
          Save. Then in your flow editor drop an <code>ai_agent</code> node and pick this profile in the Inspector.
        </p>
      </div>
    ),
  },
  {
    id: 'ai-agent-test',
    category: 'ai_agents',
    question: 'How do I test the AI Agent without calling for real?',
    tags: ['ai', 'agent', 'test', 'simulator'],
    answer: (
      <p>
        Open the AI Agent profile and click <strong>Test call</strong>. We'll dial a number you specify (your own phone) and connect you to the agent for a real conversation. Each test call costs the same as a production call — keep them short.
      </p>
    ),
  },
  {
    id: 'ai-agent-languages',
    category: 'ai_agents',
    question: 'Does the agent speak multiple languages?',
    tags: ['ai', 'language', 'es', 'en', 'spanish', 'english'],
    answer: (
      <div className="space-y-2">
        <p>
          Yes. In the profile's Identity section pick one or more languages. If you select only one (e.g. <code>es-MX</code>), the agent is locked to that language — useful for businesses where mixing languages is unprofessional.
        </p>
        <p>
          With multiple languages the agent detects the caller's language and matches.
        </p>
      </div>
    ),
  },

  // =============================================================================
  // Inbound IVR — DIDs, business hours, voicemails, recordings, webhook secret
  // =============================================================================
  {
    id: 'inbound-routing',
    category: 'inbound',
    question: 'How do callers reach my flow?',
    tags: ['inbound', 'did', 'routing'],
    cta: { label: 'Open DIDs', to: '/voice/dids' },
    answer: (
      <div className="space-y-2">
        <p>
          For each DID, you set what happens when someone calls it: open the DID row, click <strong>Inbound config</strong>, and pick a routing type:
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Flow</strong> — your IVR flow answers. Most common.</li>
          <li><strong>AI Agent</strong> — straight to an AI Agent profile (no IVR menu).</li>
          <li><strong>Transfer</strong> — forwards the call to another number or SIP URI.</li>
          <li><strong>Voicemail</strong> — always go to voicemail.</li>
        </ul>
        <p>
          You can also wire a fallback for closed-business-hours and a separate number to receive SMS notifications when a voicemail is left.
        </p>
      </div>
    ),
  },
  {
    id: 'inbound-business-hours',
    category: 'inbound',
    question: 'How do I set business hours?',
    tags: ['hours', 'schedule', 'business-hours'],
    cta: { label: 'Open Business hours', to: '/voice/business-hours' },
    answer: (
      <div className="space-y-2">
        <p>
          IVR → <strong>Business hours</strong> → toggle each day open/closed and pick start + end times. Saves as your workspace-wide default.
        </p>
        <p>
          Inbound flows can use the <code>schedule_check</code> node (in v2-native flows) to branch on whether the call is in or out of hours. When closed, the default behavior is voicemail if the DID has it enabled, otherwise the call hangs up cleanly.
        </p>
      </div>
    ),
  },
  {
    id: 'inbound-voicemails',
    category: 'inbound',
    question: 'Where do voicemails go?',
    tags: ['voicemail', 'after-hours', 'inbox'],
    cta: { label: 'Open Voicemails', to: '/voice/voicemails' },
    answer: (
      <div className="space-y-2">
        <p>
          IVR → <strong>Voicemails</strong>. Each row has the caller's number, when they called, the audio recording, and (if transcription is enabled on your plan) a text transcript.
        </p>
        <p>
          Filter by All / Unread. Click a row to play the audio inline and mark it as read.
        </p>
        <p>
          You can also receive an SMS notification when a voicemail comes in — configure the destination phone in the DID's Inbound config.
        </p>
      </div>
    ),
  },
  {
    id: 'inbound-recordings',
    category: 'inbound',
    question: "What's the difference between Voicemails and Recordings?",
    tags: ['voicemail', 'recording', 'capture'],
    answer: (
      <div className="space-y-2">
        <p>
          <strong>Voicemails</strong> — captures made when the caller couldn't reach a human (closed hours, no-answer, voicemail-only routing). One per call.
        </p>
        <p>
          <strong>Recordings</strong> — captures made by a <code>record</code> node inside your IVR flow. E.g. "Please say your account number after the beep" — that audio lands in <strong>Recordings</strong>, grouped by the flow and node that captured it. Useful for forms-by-voice.
        </p>
      </div>
    ),
  },
  {
    id: 'inbound-webhook-secret',
    category: 'inbound',
    question: 'How do I sign outbound webhooks from my IVR?',
    tags: ['webhook', 'signing', 'hmac', 'security'],
    cta: { label: 'Open Webhook secret', to: '/voice/webhook-secret' },
    answer: (
      <div className="space-y-2">
        <p>
          When your IVR flow uses a <code>webhook</code> node to call your CRM, you can sign every request so your CRM verifies it really came from us.
        </p>
        <p>
          IVR → <strong>Webhook secret</strong> → "Rotate secret". The full secret is shown <strong>once</strong> — copy it to your CRM's verifier config. Future rotations invalidate the previous secret.
        </p>
        <p>
          Signature format: <code>X-EzSender-Signature: t=&lt;unix&gt;,v1=&lt;hmac-sha256(secret, ts.body)&gt;</code> (Stripe pattern).
        </p>
      </div>
    ),
  },
];
